/**
 * FormationPackagesList — filterable list of company-formation packages.
 * Route: /control/company-setup/formation-packages
 *
 * Feb 2026 / iter-247.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Loader2, Package, Search, Copy, Trash2, Pencil, ExternalLink,
} from 'lucide-react';

import DashboardHeader from '../../components/DashboardHeader';
import BreadcrumbNav from '../../components/BreadcrumbNav';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';
import { AUTHORITIES } from './formationPackageOptions';
import { DirhamPrice } from '../../components/DirhamPrice';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_PILL = {
  active:   { ar: 'نشطة',    en: 'Active',   cls: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300' },
  draft:    { ar: 'مسودة',   en: 'Draft',    cls: 'bg-amber-500/15   border-amber-500/30   text-amber-200' },
  archived: { ar: 'مؤرشفة',  en: 'Archived', cls: 'bg-white/[0.03]   border-white/10       text-white/50' },
};

export default function FormationPackagesList() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const authorityQ = searchParams.get('authority') || '';
  const [authority, setAuthority] = useState(authorityQ);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (authority) params.set('authority', authority);
      if (status)    params.set('status', status);
      if (q.trim())  params.set('q', q.trim());
      const r = await axios.get(`${API}/admin/formation-packages?${params}`, { headers: getAuthHeaders() });
      setItems(r.data.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [authority, status]);

  const authName = useMemo(() => {
    const m = AUTHORITIES.find((x) => x.slug === authority);
    return m ? (isAr ? m.ar : m.en) : '';
  }, [authority, isAr]);

  const duplicate = async (id) => {
    try {
      const r = await axios.post(`${API}/admin/formation-packages/${id}/duplicate`, {}, { headers: getAuthHeaders() });
      toast.success(t('تم النسخ', 'Duplicated'));
      navigate(`/control/company-setup/formation-packages/${r.data.data.id}/edit`);
    } catch (err) { toast.error(err?.response?.data?.detail || err.message); }
  };

  const remove = async (id) => {
    if (!window.confirm(t('حذف نهائي للباقة؟', 'Permanently delete?'))) return;
    try {
      await axios.delete(`${API}/admin/formation-packages/${id}`, { headers: getAuthHeaders() });
      toast.success(t('تم الحذف', 'Deleted'));
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || err.message); }
  };

  return (
    <div className="min-h-screen bg-[#050510] text-white pb-16" dir={isAr ? 'rtl' : 'ltr'}>
      <DashboardHeader showBackButton backPath="/control/company-setup" />

      <div className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'إعداد الشركات', label_en: 'Company Setup', path: '/control/company-setup' },
            { label_ar: 'باقات التأسيس', label_en: 'Formation Packages', path: null },
          ]} />
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
              <Package className="w-6 h-6 text-amber-300" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">{t('باقات التأسيس', 'Formation Packages')}</h1>
              <p className="text-xs text-white/50">
                {authName ? t('حسب الجهة: ', 'Filtered by authority: ') + authName : t('كل الجهات', 'All authorities')}
              </p>
            </div>
          </div>
          <Button onClick={() => navigate(`/control/company-setup/formation-packages/new${authority ? `?authority=${authority}` : ''}`)}
                  className="bg-amber-500 hover:bg-amber-600 text-black font-bold"
                  data-testid="pkg-new-btn">
            <Plus className="w-4 h-4 me-2" />
            {t('باقة جديدة', 'New Package')}
          </Button>
        </div>

        {/* Filters */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 mb-5 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-[11px] text-white/60 mb-1 block">{t('الجهة', 'Authority')}</label>
            <select value={authority} onChange={(e) => setAuthority(e.target.value)}
                    className="w-full h-9 rounded-md bg-black/40 border border-white/10 text-white text-sm px-2"
                    data-testid="pkg-filter-authority">
              <option value="">{t('كل الجهات', 'All')}</option>
              {AUTHORITIES.map((a) => <option key={a.slug} value={a.slug}>{isAr ? a.ar : a.en}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] text-white/60 mb-1 block">{t('الحالة', 'Status')}</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}
                    className="w-full h-9 rounded-md bg-black/40 border border-white/10 text-white text-sm px-2"
                    data-testid="pkg-filter-status">
              <option value="">{t('الكل', 'All')}</option>
              <option value="active">{t('نشطة', 'Active')}</option>
              <option value="draft">{t('مسودة', 'Draft')}</option>
              <option value="archived">{t('مؤرشفة', 'Archived')}</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-[11px] text-white/60 mb-1 block">{t('بحث', 'Search')}</label>
            <div className="flex gap-2">
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder={t('اسم الباقة أو الكود', 'Package name or bundle code')}
                     onKeyDown={(e) => e.key === 'Enter' && load()}
                     data-testid="pkg-filter-q" />
              <Button onClick={load} className="bg-white/10 border border-white/10 text-white/80" data-testid="pkg-filter-search">
                <Search className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] p-16 text-center">
            <Package className="w-10 h-10 text-amber-400/40 mx-auto mb-3" />
            <p className="text-white/60 text-sm mb-3">{t('لا توجد باقات — ابدأ بإنشاء واحدة', 'No packages yet — start by creating one')}</p>
            <Button onClick={() => navigate(`/control/company-setup/formation-packages/new${authority ? `?authority=${authority}` : ''}`)}
                    className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
              <Plus className="w-4 h-4 me-2" />
              {t('باقة جديدة', 'New Package')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((pkg) => {
              const status = STATUS_PILL[pkg.status] || STATUS_PILL.draft;
              const authM  = AUTHORITIES.find((a) => a.slug === pkg.authority);
              const cheapest = (pkg.tenure_prices || []).map((r) => r.price).filter((x) => x > 0);
              const minPrice = cheapest.length ? Math.min(...cheapest) : 0;
              return (
                <div key={pkg.id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:border-amber-500/30 transition-colors"
                     data-testid={`pkg-card-${pkg.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="text-white font-bold truncate">{isAr ? pkg.name_ar : pkg.name_en}</h3>
                      <p className="text-[10px] text-amber-300 font-mono mt-0.5">{pkg.bundle_code}</p>
                    </div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] border font-semibold ${status.cls}`}>
                      {isAr ? status.ar : status.en}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[11px] text-white/60 mb-3">
                    <div>
                      <span className="text-white/40">{t('الجهة', 'Authority')}: </span>
                      <span className="text-white/80">{authM ? (isAr ? authM.ar : authM.en) : pkg.authority}</span>
                    </div>
                    <div>
                      <span className="text-white/40">{t('التأشيرات', 'Visas')}: </span>
                      <span className="text-white/80 font-mono">{pkg.included_visas || 0}</span>
                    </div>
                    <div>
                      <span className="text-white/40">{t('الأنشطة', 'Activities')}: </span>
                      <span className="text-white/80 font-mono">{pkg.max_activities || 0}</span>
                    </div>
                    <div>
                      <span className="text-white/40">{t('الأشكال', 'Legal forms')}: </span>
                      <span className="text-white/80 font-mono">{pkg.legal_forms?.length || 0}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-white/5">
                    <div>
                      <p className="text-[10px] text-white/40">{t('من', 'From')}</p>
                      <p className="text-amber-300 font-bold text-lg"><DirhamPrice value={minPrice} /></p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => navigate(`/control/company-setup/formation-packages/${pkg.id}/edit`)}
                              className="p-1.5 rounded-md hover:bg-white/5 text-white/60"
                              title={t('تعديل', 'Edit')}
                              data-testid={`pkg-edit-${pkg.id}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => duplicate(pkg.id)}
                              className="p-1.5 rounded-md hover:bg-white/5 text-white/60"
                              title={t('نسخ', 'Duplicate')}
                              data-testid={`pkg-dup-${pkg.id}`}>
                        <Copy className="w-4 h-4" />
                      </button>
                      <button onClick={() => remove(pkg.id)}
                              className="p-1.5 rounded-md hover:bg-rose-500/10 text-rose-400"
                              title={t('حذف', 'Delete')}
                              data-testid={`pkg-del-${pkg.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
